import React, { useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

interface ImageDropZoneProps {
    label: string;
    imageUrl?: string;
    imageTitle?: string;
    onFileDropped: (file: File) => Promise<void>;
    onOpenMediaPicker: () => void;
    onClear?: () => Promise<void>;
    disabled?: boolean;
    previewMaxHeight?: number;
    previewObjectFit?: 'cover' | 'contain';
}

export const ImageDropZone: React.FC<ImageDropZoneProps> = ({
    label,
    imageUrl,
    imageTitle,
    onFileDropped,
    onOpenMediaPicker,
    onClear,
    disabled,
    previewMaxHeight = 140,
    previewObjectFit = 'cover',
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleFile = async (file?: File) => {
        if (!file || disabled) {
            return;
        }

        await onFileDropped(file);
    };

    return (
        <Box
            sx={{
                border: '1px dashed',
                borderColor: isDragOver ? 'primary.main' : 'divider',
                borderRadius: 1,
                p: 1.5,
                backgroundColor: isDragOver ? 'action.hover' : 'transparent',
            }}
            onDragOver={(event) => {
                event.preventDefault();
                if (!disabled) {
                    setIsDragOver(true);
                }
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={async (event) => {
                event.preventDefault();
                setIsDragOver(false);
                await handleFile(event.dataTransfer.files?.[0]);
            }}
        >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {label}
            </Typography>

            {imageUrl ? (
                <Box
                    component="img"
                    src={imageUrl}
                    alt={imageTitle || label}
                    sx={{
                        width: '100%',
                        maxHeight: previewMaxHeight,
                        objectFit: previewObjectFit,
                        borderRadius: 1,
                        mb: 1,
                    }}
                />
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Bild hierhin ziehen oder aus Medienbibliothek waehlen
                </Typography>
            )}

            <Stack direction="row" spacing={1}>
                <Button variant="outlined" size="small" disabled={disabled} onClick={() => inputRef.current?.click()}>
                    Datei waehlen
                </Button>
                <Button variant="outlined" size="small" disabled={disabled} onClick={onOpenMediaPicker}>
                    Medienbibliothek
                </Button>
                {!!imageUrl && onClear && (
                    <Button variant="outlined" size="small" color="error" disabled={disabled} onClick={onClear}>
                        Entfernen
                    </Button>
                )}
            </Stack>

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={async (event) => {
                    await handleFile(event.target.files?.[0]);
                    event.currentTarget.value = '';
                }}
            />
        </Box>
    );
};
